import React, { useMemo } from 'react';
import { addDays, addWeeks, startOfWeek } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useSettings } from 'services/settings/SettingsContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleLeft, faAngleRight } from '@fortawesome/free-solid-svg-icons';
import { getISOWeekNumber } from 'services/utils/dateTimeUtils';
import '../../style/scss/button.scss';

interface WeekNavigationProps {
    weekStartDate: Date;
    onChange: (newDate: Date) => void;
    buttonSize?: 'small' | 'medium';
    numberOfDays?: number;
    reverse?: boolean;
}

const WeekNavigation: React.FC<WeekNavigationProps> = ({
    weekStartDate,
    onChange,
    buttonSize = 'medium',
    numberOfDays = 7,
    reverse = false
}) => {
    const { t } = useTranslation();
    const { userLocale } = useSettings();
    const todayWeekStart = startOfWeek(new Date(), { weekStartsOn: 1 });

    // Generate the days of the week
    const weekDays = useMemo(() => {
        return Array.from({ length: numberOfDays }).map((_, index) =>
            addDays(weekStartDate, index)
        );
    }, [weekStartDate, numberOfDays]);

    // Get the first date, last date and week number
    const firstDate = weekDays[0];
    const lastDate = weekDays[weekDays.length - 1];
    const weekNumber = getISOWeekNumber(firstDate);

    // Determine previous, current, next week label
    const weekDiff = Math.round(
        (weekStartDate.getTime() - todayWeekStart.getTime()) /
        (7 * 24 * 60 * 60 * 1000)
    );

    // Get the week prefix
    const getWeekPrefix = (): string | null => {
        if (weekDiff === 0) return t('date_time.general.current_week');
        if (weekDiff === -1) return t('date_time.general.previous_week');
        if (weekDiff === 1) return t('date_time.general.next_week');
        return null;
    };

    // Format the date range
    const formatRange = () => {
        const formatForLocale = (date: Date) => {
            const monthName = t(`date_time.months.short.${date.getMonth()}`);
            return userLocale === 'en-US'
                ? `${monthName} ${date.getDate()}`
                : `${date.getDate()} ${monthName}`;
        };

        const sameMonth = firstDate.getMonth() === lastDate.getMonth();

        if (sameMonth) {
            const monthName = t(`date_time.months.short.${firstDate.getMonth()}`);
            return userLocale === 'en-US'
                ? `${monthName} ${firstDate.getDate()} - ${lastDate.getDate()}`
                : `${firstDate.getDate()} - ${lastDate.getDate()} ${monthName}`;
        }

        return `${formatForLocale(firstDate)} - ${formatForLocale(lastDate)}`;
    };

    return (
        <div className={`week-navigation ${buttonSize} ${reverse ? 'reverse' : ''}`}>
            <div className="buttons">
                <button
                    className="previous"
                    onClick={() => onChange(addWeeks(weekStartDate, -1))}>
                    <FontAwesomeIcon icon={faAngleLeft} />
                </button>
                <button
                    className="next"
                    onClick={() => onChange(addWeeks(weekStartDate, 1))}>
                    <FontAwesomeIcon icon={faAngleRight} />
                </button> 
            </div>

            <div className="date">
                {`${getWeekPrefix() ? `${getWeekPrefix()}: ` : ''}${formatRange()} (week ${weekNumber})`}
            </div>

                       
        </div>
    );
};

export default WeekNavigation;